/**
 * The water's detail texture, generated at runtime (no asset to download): a seamlessly tiling
 * random wave field, built as a sum of sine waves whose wave vectors are whole numbers of cycles
 * per tile, so the edges match exactly.
 *
 * Channels (RGBA8):
 * - R, G: surface slope dh/du, dh/dv, mapped from [-1, 1] to [0, 255]. Slopes rather than a
 *   normal so several layers combine by simple addition in the shader.
 * - B: height, 0..1 (crest foam).
 * - A: a ridged second field (1 - |n|), sharp bright lines where two layers cross: caustics and
 *   foam break-up.
 *
 * The spectrum falls off with frequency like wind ripples (amplitude ~ k^-1.6) and is spread over
 * all directions; the shader layers it at several scales, angles and speeds so no repetition shows.
 */

export interface WaterNormalMapData {
    size: number;
    pixels: Uint8Array;
}

/** Small deterministic PRNG (mulberry32) so every device generates the same texture. */
function rng(seed: number): () => number {
    let a = seed >>> 0;
    return () => {
        a = (a + 0x6d2b79f5) >>> 0;
        let t = a;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

interface Wave {
    kx: number;
    ky: number;
    amp: number;
    phase: number;
}

/**
 * `spread`: standard deviation of the wave directions around the tile's x axis (radians), waves
 * running both ways; Infinity for all directions. Wind ripples are directional, which is what
 * makes them read as water rather than noise; the shader rotates each layer differently.
 */
function makeWaves(random: () => number, count: number, kMin: number, kMax: number, falloff: number, spread = Infinity): Wave[] {
    const waves: Wave[] = [];
    const seen = new Set<string>();
    let guard = 0;
    while (waves.length < count && guard++ < count * 50) {
        // Log-uniform wavelength, uniform direction; rounded to whole cycles per tile (seamless).
        const k = kMin * Math.pow(kMax / kMin, random());
        const gaussian = Math.sqrt(-2 * Math.log(Math.max(random(), 1e-9))) * Math.cos(2 * Math.PI * random());
        const angle = Number.isFinite(spread) ? (random() < 0.5 ? 0 : Math.PI) + gaussian * spread : random() * Math.PI * 2;
        const kx = Math.round(Math.cos(angle) * k);
        const ky = Math.round(Math.sin(angle) * k);
        if (kx === 0 && ky === 0) continue;
        const key = `${kx},${ky}`;
        if (seen.has(key) || seen.has(`${-kx},${-ky}`)) continue;
        seen.add(key);
        const len = Math.hypot(kx, ky);
        waves.push({ kx, ky, amp: Math.pow(len, -falloff) * (0.75 + 0.5 * random()), phase: random() * Math.PI * 2 });
    }
    return waves;
}

/**
 * Evaluate sum(amp * sin(2pi (kx u + ky v) + phase)) and its gradient over a size x size grid,
 * with the angle-addition identity so each texel costs a few multiplies per wave.
 */
function evaluate(waves: Wave[], size: number): { h: Float32Array; du: Float32Array; dv: Float32Array } {
    const n = size * size;
    const h = new Float32Array(n);
    const du = new Float32Array(n);
    const dv = new Float32Array(n);
    const sx = new Float32Array(size);
    const cx = new Float32Array(size);
    const sy = new Float32Array(size);
    const cy = new Float32Array(size);
    const tau = Math.PI * 2;
    for (const w of waves) {
        for (let i = 0; i < size; i++) {
            const a = tau * w.kx * (i / size) + w.phase;
            sx[i] = Math.sin(a);
            cx[i] = Math.cos(a);
            const b = tau * w.ky * (i / size);
            sy[i] = Math.sin(b);
            cy[i] = Math.cos(b);
        }
        const gu = w.amp * tau * w.kx;
        const gv = w.amp * tau * w.ky;
        for (let y = 0; y < size; y++) {
            const syy = sy[y];
            const cyy = cy[y];
            const row = y * size;
            for (let x = 0; x < size; x++) {
                // sin(A + B), cos(A + B)
                const s = sx[x] * cyy + cx[x] * syy;
                const c = cx[x] * cyy - sx[x] * syy;
                const idx = row + x;
                h[idx] += w.amp * s;
                du[idx] += gu * c;
                dv[idx] += gv * c;
            }
        }
    }
    return { h, du, dv };
}

/** Generate the texture (size must be a power of two for mipmapping; 128 or 256). */
export function generateWaterNormalMap(size = 256, seed = 1337): WaterNormalMapData {
    const random = rng(seed);
    // Main ripple field: 2..20 cycles per tile, directional (about +-35 degrees), slopes dominated
    // by the longer waves so a layer reads as ripple trains instead of static.
    const ripple = evaluate(makeWaves(random, 56, 2, 20, 1.75, 0.6), size);
    // Ridged field for caustics / foam: a narrower band of finer cells.
    const cells = evaluate(makeWaves(random, 40, 4, 14, 0.6), size).h;

    const n = size * size;
    let slopeMax = 1e-6;
    let hMin = Infinity;
    let hMax = -Infinity;
    let cMax = 1e-6;
    for (let i = 0; i < n; i++) {
        slopeMax = Math.max(slopeMax, Math.abs(ripple.du[i]), Math.abs(ripple.dv[i]));
        hMin = Math.min(hMin, ripple.h[i]);
        hMax = Math.max(hMax, ripple.h[i]);
        cMax = Math.max(cMax, Math.abs(cells[i]));
    }
    // Normalise slopes by a high percentile-ish bound (90% of the max) so the bulk of the range is used.
    const slopeScale = 1 / (slopeMax * 0.9);
    const hSpan = Math.max(hMax - hMin, 1e-6);
    const pixels = new Uint8Array(n * 4);
    const to8 = (v: number) => Math.max(0, Math.min(255, Math.round(v * 255)));
    for (let i = 0; i < n; i++) {
        pixels[i * 4] = to8(0.5 + 0.5 * Math.max(-1, Math.min(1, ripple.du[i] * slopeScale)));
        pixels[i * 4 + 1] = to8(0.5 + 0.5 * Math.max(-1, Math.min(1, ripple.dv[i] * slopeScale)));
        pixels[i * 4 + 2] = to8((ripple.h[i] - hMin) / hSpan);
        pixels[i * 4 + 3] = to8(1 - Math.abs(cells[i]) / cMax);
    }
    return { size, pixels };
}
